import { OrderTypeEnum } from '@flipster/apis/types';
import {
  deletePendingPositionApi,
  postOneWayPartialTpslApi,
  postOrderPartialTpslApi,
  putOrderPartialTpslApi,
} from '@flipster/apis/v2/trade';
// ROLLBACK(last-price): triggerPriceType 전송 재활성화 시 복원.
// import type { TriggerPriceType } from '@flipster/apis/v2/trade/types';
import { ConditionalOrderTypeEnum, type Order } from '@flipster/r230522/original/order';
import { refPositionTimes } from '@flipster/r230522/original/server-time';
import Num from '@flipster/utils/num';
import type { V2OrderSideType } from '@flipster/utils/types/base';

// type-only: 실행 필드 형태의 정의는 tpsl-modal의 순수 빌더(tpsl-request.ts)가 소유한다.
import type {
  PrefixedExecutionFields,
  TpslExecutionFieldsBase,
} from '../../../tpsl-modal/tpsl-form-content/tpsl-request';
import { buildTradeRequestBase } from '../base-params';

/**
 * TP/SL 원시 요청 함수들 (구 tpsl-modal/tpsl-form-content/api.tsx의 leaf 함수 + chart-order-drag의
 * deleteThenCreateTpsl). 모달/차트 오케스트레이터는 atom·scope에서 값을 읽어 여기로 위임한다.
 * 페이로드 형태는 기존과 byte-동일하게 보존한다.
 */

interface TpslRequestContext {
  attribution: string | undefined;
  /** copy-trading 서브계정. 생략(undefined) 시 메인 계정. */
  accountId?: number;
}

export const upsertOpenPositionTpslMultiplePositions = ({
  symbol,
  slot,
  side,
  triggerPrice,
  triggerDirection,
  // triggerPriceType, // ROLLBACK(last-price)
  orderQty,
  executionFields,
  attribution,
  accountId,
}: TpslRequestContext & {
  symbol: string;
  slot: number;
  side: V2OrderSideType;
  triggerPrice: string;
  triggerDirection: 'Up' | 'Down';
  // triggerPriceType: TriggerPriceType; // ROLLBACK(last-price)
  orderQty: string | undefined;
  executionFields: PrefixedExecutionFields;
}) =>
  postOrderPartialTpslApi(symbol, {
    ...buildTradeRequestBase(attribution),
    slot,
    side,
    // ROLLBACK(last-price): 원본 — orders: [{ triggerPrice, triggerDirection, triggerPriceType, orderQty, ...executionFields }],
    orders: [{ triggerPrice, triggerDirection, orderQty, ...executionFields }],
    ...refPositionTimes[symbol],
    accountId,
  });

export const upsertOpenPositionTpslOneWay = ({
  symbol,
  side,
  orderQty,
  triggerPrice,
  triggerDirection,
  // triggerPriceType, // ROLLBACK(last-price)
  executionBase,
  attribution,
  accountId,
}: TpslRequestContext & {
  symbol: string;
  side: V2OrderSideType;
  orderQty: string | undefined;
  triggerPrice: string;
  triggerDirection: 'Up' | 'Down';
  // triggerPriceType: TriggerPriceType; // ROLLBACK(last-price)
  executionBase: TpslExecutionFieldsBase;
}) => {
  // one-way/partial-tpsl uses the unprefixed shape (a single TP/SL per call).
  // Market path mirrors the prefixed path: omit `orderType` + limit fields so
  // the server uses its Market default (per types.ts:507 doc).
  const { orderType, limitPrice, priceMatchStrategy } = executionBase;
  return postOneWayPartialTpslApi(symbol, {
    ...buildTradeRequestBase(attribution),
    side,
    orderQty,
    triggerPrice,
    triggerDirection,
    // triggerPriceType, // ROLLBACK(last-price)
    ...(orderType === 'Limit' ? { orderType, limitPrice, priceMatchStrategy } : {}),
    ...refPositionTimes[symbol],
    accountId,
  });
};

export const upsertPendingPositionTpsl = ({
  symbol,
  parentId,
  type,
  triggerPrice,
  triggerDirection,
  // triggerPriceType, // ROLLBACK(last-price)
  orderQty,
  executionFields,
  attribution,
  accountId,
}: TpslRequestContext & {
  symbol: string;
  parentId: string;
  type: 'OrderTp' | 'OrderSl';
  triggerPrice: string;
  triggerDirection: 'Up' | 'Down';
  // triggerPriceType: TriggerPriceType; // ROLLBACK(last-price)
  orderQty: string | undefined;
  executionFields: PrefixedExecutionFields;
}) =>
  putOrderPartialTpslApi(symbol, parentId, {
    ...buildTradeRequestBase(attribution),
    accountId,
    type,
    // ROLLBACK(last-price): 원본 — trigger: { triggerPrice, triggerDirection, triggerPriceType, orderQty, ...executionFields },
    trigger: { triggerPrice, triggerDirection, orderQty, ...executionFields },
  });

export const deleteOpenPositionTpsl = ({
  symbol,
  orderId,
  attribution,
  accountId,
}: TpslRequestContext & { symbol: string; orderId: string }) =>
  deletePendingPositionApi(symbol, orderId, {
    ...buildTradeRequestBase(attribution),
    accountId,
  });

export const deletePendingPositionTpsl = ({
  symbol,
  parentId,
  type,
  attribution,
  accountId,
}: TpslRequestContext & { symbol: string; parentId: string; type: 'OrderTp' | 'OrderSl' }) =>
  putOrderPartialTpslApi(symbol, parentId, {
    ...buildTradeRequestBase(attribution),
    accountId,
    type,
  });

/**
 * standalone Position TP/SL의 트리거가 이동: 삭제 후 재생성 (구 chart-order-drag deleteThenCreateTpsl).
 * 서버가 update를 delete→create로 처리하는 것을 클라이언트에서 수행한다.
 */
export async function replaceTpslTriggerPrice(
  order: Order,
  newTriggerPrice: string,
  tradeMode: string,
  attribution: string | undefined,
  symbol: string,
): Promise<void> {
  await deletePendingPositionApi(symbol, order.orderId, buildTradeRequestBase(attribution));

  const orderQty = Num(order.qty || '0').isZero() ? undefined : order.qty;
  // Standalone Position TP/SL stores execution as STOP_LIMIT/STOP_MARKET
  // (see tpsl-modal/state/index.ts:260). BBO rows are flagged by
  // `priceMatchStrategy`; their `price` is unset until the matcher stamps it
  // at trigger time, so we mirror the modal: send null limitPrice + the
  // strategy for BBO, and the user-set price + null strategy otherwise.
  const isLimit = order.orderType === OrderTypeEnum.StopLimit;
  const isBbo = isLimit && Boolean(order.priceMatchStrategy);
  const limitPrice = isBbo ? null : (order.price ?? null);
  const priceMatchStrategy = isBbo ? (order.priceMatchStrategy ?? null) : null;
  const isTp = order.conditionalOrderType === ConditionalOrderTypeEnum.PositionTp;

  if (tradeMode === 'OneWay') {
    // one-way/partial-tpsl uses the unprefixed shape. Omit execution fields for
    // Market so the server uses its Market default (per types.ts:507).
    await postOneWayPartialTpslApi(symbol, {
      ...buildTradeRequestBase(attribution),
      side: order.side,
      triggerPrice: newTriggerPrice,
      triggerDirection: order.triggerDirection,
      orderQty,
      ...(isLimit ? { orderType: 'Limit', limitPrice, priceMatchStrategy } : {}),
      ...refPositionTimes[symbol],
    });
  } else {
    // multi-positions uses prefixed (orderTp*/orderSl*) execution fields.
    const executionFields = isLimit
      ? isTp
        ? {
            orderTpOrderType: 'Limit' as const,
            orderTpLimitPrice: limitPrice,
            orderTpPriceMatchStrategy: priceMatchStrategy,
          }
        : {
            orderSlOrderType: 'Limit' as const,
            orderSlLimitPrice: limitPrice,
            orderSlPriceMatchStrategy: priceMatchStrategy,
          }
      : {};
    await postOrderPartialTpslApi(symbol, {
      ...buildTradeRequestBase(attribution),
      slot: order.slot,
      side: order.side,
      orders: [
        {
          triggerPrice: newTriggerPrice,
          triggerDirection: order.triggerDirection,
          orderQty,
          ...executionFields,
        },
      ],
      ...refPositionTimes[symbol],
    });
  }
}
